import { useCallback, useMemo } from 'react';
import { bindPopover } from 'material-ui-popup-state/hooks';
import Popover from '@mui/material/Popover';

import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useGetMembershipOffers } from '@occc-app/dataAccessLayer/membership/queries';
import { Earning_Mechanism_Typename, OfferFilter } from '@occc/graphql';

import { InProgressOffersTable } from './in-progress-offers-table.component';
import { InProgressOffersFilterForm } from './in-progress-offers-filter-form.component';
import { headers } from '../in-progress-offers.constants';
import { makeFields } from '../in-progress-offers.utils';
import { useProgressOffersEligibility } from '../in-progress-offers.hook';
import {
  InProgressOffersFilterFormProps,
  InProgressOffersFilterFormValues,
} from '../in-progress-offers.types';

import {
  IN_PROGRESS_OFFERS_BEFORE_PARAM,
  IN_PROGRESS_OFFERS_AFTER_PARAM,
} from '../../../../constants';

export type InProgressOffersContainerProps = {
  memberId: string;
  searchText: string | undefined;
  popoverProps: ReturnType<typeof bindPopover>;
};

export const InProgressOffersContainer = ({
  memberId,
  searchText,
  popoverProps,
}: InProgressOffersContainerProps) => {
  const [eligibility, setEligibility] = useProgressOffersEligibility();
  const [paginationInfo, onPageChange] = usePaginationParams({
    beforeParamName: IN_PROGRESS_OFFERS_BEFORE_PARAM,
    afterParamName: IN_PROGRESS_OFFERS_AFTER_PARAM,
  });

  const filter: OfferFilter = useMemo((): OfferFilter => {
    return {
      search: searchText,
      deleted: false,
      earningMechanismTypenameIncludes: [
        Earning_Mechanism_Typename.EarningMechanismTotalSpendOverTime,
        Earning_Mechanism_Typename.EarningMechanismVarietyPurchaseOverTime,
        Earning_Mechanism_Typename.EarningMechanismVisits,
        Earning_Mechanism_Typename.EarningMechanismTotalEligiblePurchaseOverTime,
      ],
      isEligible: eligibility === 'eligible',
    };
  }, [searchText, eligibility]);

  const { data, loading, error, paginationProps } = useGetMembershipOffers({
    membershipId: memberId,
    filter,
    paginationInfo,
    onPageChange,
  });

  const onSubmit: InProgressOffersFilterFormProps['onSubmit'] = useCallback(
    ({ eligibility: value }) => {
      setEligibility(value);
      popoverProps.onClose();
    },
    [setEligibility, popoverProps],
  );

  const onReset = useCallback(() => {
    setEligibility('eligible');
    popoverProps.onClose();
  }, [setEligibility, popoverProps]);

  const filterInitialValues: InProgressOffersFilterFormValues = useMemo(() => {
    return {
      eligibility,
    };
  }, [eligibility]);

  return (
    <>
      <InProgressOffersTable
        offers={data}
        error={error}
        loading={loading}
        paginationProps={paginationProps}
        headers={headers}
        makeFields={makeFields}
      />
      <Popover
        {...popoverProps}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        keepMounted
      >
        <InProgressOffersFilterForm
          onSubmit={onSubmit}
          onReset={onReset}
          initialValues={filterInitialValues}
        />
      </Popover>
    </>
  );
};
